import { useState, useEffect } from 'react';
import {
  DxToggle,
  DxContainer,
  DxSingleSelect,
  DxSingleSelectItem,
  DxSpacer,
  DxText,
  DxIcon,
  DxGrid,
  DxMediaQueryValue,
} from '@dvag/design-system-react';
import { checkIcon } from 'utils/util';
import i18next from 'i18next';

import '../../style.css';

export const AssetsSection = ({
  enabled,
  onToggle,
  viewType,
  customerViewType,
  onViewChange,
  onViewTypeChange,
  readMode,
}: {
  enabled: boolean;
  onToggle: (checked: boolean) => void;
  viewType?: string;
  customerViewType?: string;
  onViewChange?: (value: string) => void;
  onViewTypeChange?: (value: string) => void;
  readMode: boolean;
}) => {
  const [selectedOption, setSelectedOption] = useState(viewType ?? 'CONTRACT_VIEW');
  const [selectedViewType, setSelectedViewType] = useState(customerViewType ?? 'EXPANDED_VIEW');

  useEffect(() => {
    if (viewType) {
      setSelectedOption(viewType);
    }
    if (customerViewType) {
      setSelectedViewType(customerViewType);
    }
  }, [viewType, customerViewType]);

  const handleViewChange = (value: string) => {
    setSelectedOption(value);
    if (onViewChange) {
      onViewChange(value);
    }
  };

  const handleViewTypeChange = (value: string) => {
    setSelectedViewType(value);
    if (onViewTypeChange) {
      onViewTypeChange(value);
    }
  };

  return (
    <>
      <DxContainer color="transparent" className="settings-section">
        <DxIcon
          icon={checkIcon('einkaufswagen')}
          size={24}
          className="settings-section-icon"
          color="primary"
        />
        <DxContainer color="transparent" className="settings-section-content">
          <DxText type="Infotext-Small" color="gray-66">
            {i18next.t('vpDigitalSettings.assets.labelSmall')}
          </DxText>
          <DxText type="Paragraph-Standard">
            {i18next.t('vpDigitalSettings.assets.showOverview')}
          </DxText>
          <DxText type="Infotext-Small" className="settings-sublabel" color="gray-66">
            {i18next.t('vpDigitalSettings.assets.overviewDescription')}
          </DxText>
        </DxContainer>
        <DxToggle
          checked={enabled}
          onCheckedChange={(e) => onToggle(e.detail)}
          data-testid="assets-toggle"
          disabled={readMode}
        />
      </DxContainer>
      <DxSpacer size="16" />

      {enabled && (
        <DxContainer color="transparent" style={{ padding: '0 0 16px 40px' }}>
          <DxGrid mq1="12/12/*" mq2="12/12/*" mq3="4-8/*" mq4="4-8/*" mq5="4-8/*" rowgap="16">
            <DxContainer color="transparent" style={{ marginLeft: '8px', width: '100%' }}>
              <DxText type="Paragraph-Standard">
                {i18next.t('vpDigitalSettings.assets.setDefaultView')}
              </DxText>
              <DxSpacer />
              <DxText type="Infotext-Small" color="gray-66">
                {i18next.t('vpDigitalSettings.assets.chooseOnOpen')}
              </DxText>
            </DxContainer>

            <DxContainer
              color="transparent"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '35px',
                alignItems: 'flex-end',
              }}
            >
              <DxContainer>
                <DxMediaQueryValue
                  property="style"
                  mq1="max-width:598px;width:100%"
                  mq2="max-width:598px;width:100%"
                  mq3="max-width:525px;width:100%"
                  mq4="max-width:525px;width:100%"
                  mq5="max-width:525px;width:100%"
                />
                <DxSingleSelect
                  size="s"
                  id="view-selection"
                  data-testid="view-selection"
                  value={selectedOption}
                  onValueChange={(e) => handleViewChange(e.detail)}
                  columns={2}
                  disabled={readMode}
                >
                  <DxMediaQueryValue property="columns" mq1="1" mq2="1" mq3="2" mq4="2" mq5="2" />

                  <DxSingleSelectItem
                    value="CONTRACT_VIEW"
                    label={i18next.t('vpDigitalSettings.assets.contractOverview')}
                    data-testid="contract-view-option"
                    disabled={readMode}
                  />
                  <DxSingleSelectItem
                    value="CUSTOMER_VIEW"
                    label={i18next.t('vpDigitalSettings.assets.customerOverview')}
                    data-testid="customer-view-option"
                    disabled={readMode}
                  />
                </DxSingleSelect>
              </DxContainer>

              {selectedOption === 'CUSTOMER_VIEW' && (
                <DxContainer>
                  <DxMediaQueryValue
                    property="style"
                    mq1="max-width:598px;width:100%"
                    mq2="max-width:598px;width:100%"
                    mq3="max-width:525px;width:100%"
                    mq4="max-width:525px;width:100%"
                    mq5="max-width:525px;width:100%"
                  />
                  <DxSingleSelect
                    size="s"
                    id="view-type-selection"
                    data-testid="view-type-selection"
                    value={selectedViewType}
                    onValueChange={(e) => handleViewTypeChange(e.detail)}
                    columns={2}
                    disabled={readMode}
                  >
                    <DxMediaQueryValue property="columns" mq1="1" mq2="1" mq3="2" mq4="2" mq5="2" />

                    <DxSingleSelectItem
                      value="EXPANDED_VIEW"
                      label={i18next.t('vpDigitalSettings.assets.expandedView')}
                      data-testid="expanded-view-option"
                      disabled={readMode}
                    />
                    <DxSingleSelectItem
                      value="REDUCED_VIEW"
                      label={i18next.t('vpDigitalSettings.assets.reducedView')}
                      data-testid="reduced-view-option"
                      disabled={readMode}
                    />
                  </DxSingleSelect>
                </DxContainer>
              )}
            </DxContainer>
          </DxGrid>
        </DxContainer>
      )}
    </>
  );
};
